import { useStore } from '@src/stores-v2/StoreContext';
import { MuxPauseIcon } from '@src/svgs/MuxPauseIcon';
import { MuxPlayIcon } from '@src/svgs/MuxPlayIcon';
import { extractErrorInfo, isAbortError } from '@src/utility/error';
import { LogManager } from '@src/utility/Logger';
import { useEffect, useRef, useState } from 'react';

import { SamplePlayButton, SampleUnavailable } from './samplePlayStyles';

const logger = LogManager.getLogger('aiVoices:LanguageSamplePlayButton');

// Compact play/pause control that streams a voice's pre-saved sample for one
// language (DE8 / PLAT-10502) — used per row in the Language Voice Defaults tab.
// Lazily resolves the signed sample URL on first play; a voice/language with no
// generated sample renders as a disabled "unavailable" control (never errors).
interface LanguageSamplePlayButtonProps {
  aiVoiceId: string;
  /** BCP-47 language code to fetch the sample for. */
  language: string;
  disabled?: boolean;
  /** Diameter in px; 28 fits a picker row, 24 a dense list row. */
  size?: number;
  /** The stage's filled play, for a play that stands alone in its row. */
  prominent?: boolean;
}

const LanguageSamplePlayButton = ({
  aiVoiceId,
  language,
  disabled = false,
  size = 28,
  prominent = false,
}: LanguageSamplePlayButtonProps) => {
  const { datoClientStore } = useStore();
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [playing, setPlaying] = useState(false);

  // A change of voice/language invalidates any resolved URL + playback.
  useEffect(() => {
    audioRef.current?.pause();
    audioRef.current = null;
    setUrl(null);
    setUnavailable(false);
    setPlaying(false);
  }, [aiVoiceId, language]);

  // Stop playback on unmount so a sample can't keep playing after its row/modal
  // is gone (matches VoiceInfoCard's cleanup).
  useEffect(
    () => () => {
      audioRef.current?.pause();
      audioRef.current = null;
    },
    [],
  );

  const play = (src: string) => {
    const audio = new Audio(src);
    audioRef.current = audio;
    audio.onended = () => setPlaying(false);
    audio.onpause = () => setPlaying(false);
    audio
      .play()
      .then(() => setPlaying(true))
      .catch(err => {
        logger.warn('sample play failed', extractErrorInfo(err));
        setPlaying(false);
      });
  };

  const handleClick = async () => {
    if (playing) {
      audioRef.current?.pause();
      return;
    }
    if (url) {
      play(url);
      return;
    }
    setLoading(true);
    try {
      const result = await datoClientStore.dubbingRepository.getVoiceSampleUrl({
        aiVoiceId,
        language,
      });
      if (!result) {
        setUnavailable(true);
        return;
      }
      setUrl(result.audioUrl);
      play(result.audioUrl);
    } catch (err) {
      if (isAbortError(err)) return;
      logger.error('getVoiceSampleUrl failed', extractErrorInfo(err));
      setUnavailable(true);
    } finally {
      setLoading(false);
    }
  };

  if (unavailable) {
    return (
      <SampleUnavailable $size={size} title="No sample generated yet">
        —
      </SampleUnavailable>
    );
  }

  return (
    <SamplePlayButton
      type="button"
      $size={size}
      $playing={playing}
      $prominent={prominent}
      onClick={handleClick}
      disabled={disabled || loading}
      aria-label={playing ? 'Pause sample' : 'Play sample'}
    >
      {playing ? (
        <MuxPauseIcon width="12" height="12" fillColor="currentColor" />
      ) : (
        <MuxPlayIcon width="12" height="12" fillColor="currentColor" />
      )}
    </SamplePlayButton>
  );
};

export default LanguageSamplePlayButton;
